import { useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useWorld } from "@/context/WorldContext";
import { api, formatApiError } from "@/lib/api";
import { REL_TYPES } from "@/lib/constants";

const Pick = ({ value, onChange, options, placeholder, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="bg-panel">
      {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-option-${o.value}`}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const RelationshipForm = ({ chars, rels }) => {
  const { world, reload, scope } = useWorld();
  const [f, setF] = useState({ source_id: "", type: "ally", target_id: "", description: "" });
  const opts = chars.map((c) => ({ value: c.id, label: c.title }));
  const name = (id) => chars.find((c) => c.id === id)?.title;

  const add = async (e) => {
    e.preventDefault();
    if (!f.source_id || !f.target_id) return toast.error("Choose two characters");
    try {
      await api.post(`/worlds/${world.id}/relationships`, f, { params: scope });
      await reload();
      setF({ ...f, description: "" });
      toast.success("Bond recorded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const remove = async (id) => {
    await api.delete(`/relationships/${id}`);
    await reload();
    toast.success("Bond severed");
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={add} className="rounded-xl border border-line bg-panel p-5 space-y-3" data-testid="relationship-form">
        <h3 className="font-display text-xl text-amber-100">Forge a bond</h3>
        <div className="grid grid-cols-3 gap-2">
          <Pick value={f.source_id} onChange={(v) => setF({ ...f, source_id: v })} options={opts} placeholder="Character" testId="rel-source-select" />
          <Pick value={f.type} onChange={(v) => setF({ ...f, type: v })} options={Object.keys(REL_TYPES).filter((t) => t !== "ship").map((t) => ({ value: t, label: t }))} placeholder="Type" testId="rel-type-select" />
          <Pick value={f.target_id} onChange={(v) => setF({ ...f, target_id: v })} options={opts} placeholder="Character" testId="rel-target-select" />
        </div>
        <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Optional note: sworn on the drowned altar" data-testid="rel-description-input" />
        <Button type="submit" className="rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent" data-testid="add-relationship-btn"><Plus className="w-4 h-4" /> Add bond</Button>
      </form>
      <div className="rounded-xl border border-line bg-panel p-5" data-testid="relationship-list">
        <h3 className="font-display text-xl text-amber-100 mb-3">All bonds ({rels.length})</h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {rels.map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-sm text-slate-300" data-testid={`relationship-row-${r.id}`}>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: REL_TYPES[r.type] }} />
              <span className="truncate">{name(r.source_id)} <span className="font-code text-xs text-slate-500">{r.type}</span> {name(r.target_id)}</span>
              {r.endgame && <span className="ml-auto shrink-0 text-[10px] font-code uppercase tracking-wider text-amber-400 border border-amber-500/40 rounded-full px-2 py-0.5" data-testid={`relationship-endgame-${r.id}`}>endgame</span>}
              {r.origin !== "manual" ? (
                <span className={`${r.endgame ? "" : "ml-auto "}shrink-0 text-[10px] font-code uppercase tracking-wider text-slate-500 border border-line rounded-full px-2 py-0.5`} data-testid={`relationship-origin-${r.id}`}>{r.origin === "ship" ? "from ship" : "from infobox"}</span>
              ) : (
                <button onClick={() => remove(r.id)} className="ml-auto text-slate-500 hover:text-rose-400" data-testid={`delete-relationship-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ))}
          {rels.length === 0 && <p className="text-xs text-slate-500 italic">No bonds yet.</p>}
        </div>
      </div>
    </div>
  );
};
